import React from 'react';
import Box from '@mui/joy/Box';
import Stack from '@mui/joy/Stack';
import Typography from '@mui/joy/Typography';
import Button from '@mui/joy/Button';
import FormControl from '@mui/joy/FormControl';
import FormLabel from '@mui/joy/FormLabel';
import Textarea from '@mui/joy/Textarea';
import Input from '@mui/joy/Input';
import Sheet from '@mui/joy/Sheet';
import Chip from '@mui/joy/Chip';
import CircularProgress from '@mui/joy/CircularProgress';
import {formatDateTime} from '../../../common/dateTime';
import ConfirmDialog from '../../../components/confirmDialog/ConfirmDialog';
import {useUserAccessBlacklist} from '../../accessBlacklist/useUserAccessBlacklist';

interface Props {
    userId: bigint;
    refreshKey?: number;
    onChanged?: () => void;
}

export function UserAccessBlacklistTab({ userId, refreshKey = 0, onChanged }: Props) {
    const { row, loading, isBlocked, block, unblock } = useUserAccessBlacklist(userId, refreshKey);
    const [reason, setReason] = React.useState('');
    const [expires, setExpires] = React.useState('');
    const [saving, setSaving] = React.useState(false);
    const [deleteOpen, setDeleteOpen] = React.useState(false);

    const add = async () => {
        setSaving(true);
        try {
            const ok = await block(reason, expires ? new Date(expires).toISOString() : null);
            if (ok) {
                setReason('');
                setExpires('');
                onChanged?.();
            }
        } finally {
            setSaving(false);
        }
    };

    const remove = async () => {
        setSaving(true);
        try {
            const ok = await unblock();
            if (ok) {
                setDeleteOpen(false);
                onChanged?.();
            }
        } finally {
            setSaving(false);
        }
    };

    return (
        <Box sx={{ width: '100%' }}>
            <Sheet variant="outlined" sx={{ p: 2, borderRadius: 8, minHeight: 140 }}>
                {loading ? (
                    <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', p: 4 }}>
                        <CircularProgress size="lg" />
                    </Box>
                ) : isBlocked && row ? (
                    <Stack spacing={1}>
                        <Stack direction="row" spacing={1} alignItems="center">
                            <Typography level="title-sm">Текущий статус</Typography>
                            <Chip size="sm" color="danger" variant="solid">Доступ заблокирован</Chip>
                        </Stack>
                        <Typography level="body-sm">Причина: {row.reason}</Typography>
                        <Typography level="body-xs" color="neutral">Кто добавил: {row.createdBy}</Typography>
                        <Typography level="body-xs" color="neutral">Создано: {formatDateTime(row.created)}</Typography>
                        <Typography level="body-xs" color="neutral">
                            Истекает: {row.expiresAt ? formatDateTime(row.expiresAt) : 'бессрочно'}
                        </Typography>
                        <Typography level="body-xs" color="neutral">
                            Последняя попытка: {row.lastAttemptAt ? formatDateTime(row.lastAttemptAt) : 'нет'}
                        </Typography>
                        <Button
                            size="sm"
                            color="danger"
                            sx={{ alignSelf: 'flex-start', mt: 1 }}
                            disabled={saving}
                            onClick={() => setDeleteOpen(true)}
                        >
                            Разблокировать доступ
                        </Button>
                    </Stack>
                ) : (
                    <Stack spacing={1}>
                        <Typography level="title-sm">Текущий статус</Typography>
                        <Typography level="body-sm">Пользователь не в черном списке доступа</Typography>
                        <FormControl>
                            <FormLabel>Причина</FormLabel>
                            <Textarea minRows={2} value={reason} onChange={(e) => setReason(e.target.value)} />
                        </FormControl>
                        <FormControl>
                            <FormLabel>Истекает (необязательно)</FormLabel>
                            <Input type="datetime-local" value={expires} onChange={(e) => setExpires(e.target.value)} />
                        </FormControl>
                        <Button
                            size="sm"
                            color="danger"
                            onClick={() => add().catch(console.error)}
                            disabled={!reason.trim() || saving}
                            loading={saving}
                        >
                            Заблокировать доступ (403)
                        </Button>
                    </Stack>
                )}
            </Sheet>
            <ConfirmDialog
                title="Подтверждение"
                open={deleteOpen}
                setOpen={setDeleteOpen as unknown as (e: boolean) => void}
                onConfirm={() => { remove().catch(console.error); }}
            >
                {`Разблокировать доступ пользователю ${userId.toString()}? Он снова сможет ходить в API.`}
            </ConfirmDialog>
        </Box>
    );
}
